/**
 * Shared icon and color definitions for features across the application.
 * This ensures consistency between the sidebar, quick access links, and recent items.
 */
import {
  Bird,
  BookText,
  Bot,
  CheckSquare,
  Drama,
  FlaskConical,
  FolderOpen,
  Home,
  ListChecks,
  ListTree,
  type LucideIcon,
  Pencil,
  Percent,
  Play,
  PlayCircle,
  Settings,
  Table,
  TrainFront,
  TrendingUp,
  Workflow,
  Zap,
} from "lucide-react";

export type FeatureKey =
  | "home"
  | "analytics"
  | "traces"
  | "traces_v2"
  | "simulations"
  | "agent_testing"
  | "scenarios"
  | "simulation_runs"
  | "suites"
  | "experiments"
  | "online_evaluations"
  | "workflows"
  | "prompts"
  | "datasets"
  | "annotations"
  | "settings"
  | "agents"
  | "evaluators"
  | "automations"
  | "gateway";

export type FeatureConfig = {
  icon: LucideIcon;
  color: string;
  label: string;
};

/**
 * Central configuration for feature icons and colors.
 * Used by MainMenu and RecentItemsSection. A feature is a category, not a
 * status, so every feature takes the neutral icon colour.
 */
export const featureIcons: Record<FeatureKey, FeatureConfig> = {
  home: {
    icon: Home,
    color: "fg.2",
    label: "Home",
  },
  analytics: {
    icon: TrendingUp,
    color: "fg.2",
    label: "Analytics",
  },
  traces: {
    icon: ListTree,
    color: "fg.2",
    label: "Traces",
  },
  traces_v2: {
    icon: Bird,
    color: "fg.2",
    label: "Trace Explorer",
  },
  simulations: {
    icon: Play,
    color: "fg.2",
    label: "Simulations",
  },
  agent_testing: {
    icon: ListChecks,
    color: "fg.2",
    label: "Agent Testing",
  },
  scenarios: {
    icon: Drama,
    color: "fg.2",
    label: "Scenarios",
  },
  simulation_runs: {
    icon: PlayCircle,
    color: "fg.2",
    label: "Runs",
  },
  suites: {
    icon: FolderOpen,
    color: "fg.2",
    label: "Run Plans",
  },
  experiments: {
    icon: FlaskConical,
    color: "fg.2",
    label: "Experiments",
  },
  online_evaluations: {
    icon: CheckSquare,
    color: "fg.2",
    label: "Online evaluations",
  },
  workflows: {
    icon: Workflow,
    color: "fg.2",
    label: "Workflows",
  },
  prompts: {
    icon: BookText,
    color: "fg.2",
    label: "Prompts",
  },
  datasets: {
    icon: Table,
    color: "fg.2",
    label: "Datasets",
  },
  annotations: {
    icon: Pencil,
    color: "fg.2",
    label: "Annotations",
  },
  settings: {
    icon: Settings,
    color: "fg.2",
    label: "Settings",
  },
  agents: {
    icon: Bot,
    color: "fg.2",
    label: "Agents",
  },
  evaluators: {
    icon: Percent,
    color: "fg.2",
    label: "Evaluators",
  },
  automations: {
    icon: Zap,
    color: "fg.2",
    label: "Automations",
  },
  gateway: {
    icon: TrainFront,
    color: "fg.2",
    label: "AI Gateway",
  },
};

/**
 * Map from RecentItemType to FeatureKey for consistent icons/colors.
 */
export const recentItemTypeToFeature: Record<string, FeatureKey> = {
  prompt: "prompts",
  workflow: "workflows",
  dataset: "datasets",
  evaluation: "online_evaluations",
  annotation: "annotations",
  simulation: "simulations",
};
